/**
 * Game-card title + subtitle helpers — used by Home recent feed,
 * History list, Leaderboard side-views, anywhere we render a
 * compact game summary.
 *
 * Title rules:
 *   - 2 players → "A vs B"
 *   - 3 players → "A, B vs C"
 *   - 4+ players → "A, B, C +N"
 *   - Solo → "vs. Atom"
 *
 * Subtitle rules:
 *   - "<rounds> rounds · <mode>" — mode is solo / live / async / classic
 *   - For your own games: "<wld> · <yourScore>–<theirScore>" prefix optional
 */

export interface GameTitleInput {
  players: Array<{ id: string; name: string; isBot?: boolean }>;
  /** Format from the perspective of this user (puts them first if present). */
  perspectiveUid?: string;
  /** Solo flag — when true, title is "vs. Atom" regardless of player count. */
  isSolo?: boolean;
}

export function buildGameTitle(input: GameTitleInput): string {
  const { players, perspectiveUid, isSolo } = input;
  if (!players || players.length === 0) return 'Game';
  if (isSolo || players.some(p => p.isBot && p.name === 'Atom')) {
    const human = players.find(p => !p.isBot);
    if (human) {
      return perspectiveUid && human.id === perspectiveUid ? 'You vs. Atom' : `${human.name} vs. Atom`;
    }
    return 'Solo vs. Atom';
  }
  // Re-order so the perspective uid is first ("You vs. X" vibe)
  const ordered = perspectiveUid
    ? [
        ...players.filter(p => p.id === perspectiveUid),
        ...players.filter(p => p.id !== perspectiveUid),
      ]
    : players.slice();
  const firstName = (p: { name: string; id: string }) =>
    perspectiveUid && p.id === perspectiveUid ? 'You' : p.name;
  if (ordered.length === 1) return firstName(ordered[0]);
  if (ordered.length === 2) return `${firstName(ordered[0])} vs. ${firstName(ordered[1])}`;
  if (ordered.length === 3) return `${firstName(ordered[0])}, ${firstName(ordered[1])} vs. ${firstName(ordered[2])}`;
  // 4+ players: "A, B, C +N more"
  const visible = ordered.slice(0, 3).map(firstName);
  const extra = ordered.length - 3;
  return `${visible.join(', ')} +${extra}`;
}

export function buildGameSubtitle(input: {
  rounds: number;
  mode?: string;             // 'solo' | 'multiplayer' | undefined
  isAsync?: boolean;
  status?: string;           // 'waiting' | 'lobby' | 'playing' | 'completed'
  currentRound?: number;
  totalRounds?: number;
  playersCount?: number;
}): string {
  const parts: string[] = [];
  // Round indicator: live games show progress, finished games show total.
  if (input.status === 'playing' && input.currentRound && input.totalRounds) {
    parts.push(`Round ${input.currentRound}/${input.totalRounds}`);
  } else if (input.rounds > 0) {
    parts.push(`${input.rounds} round${input.rounds === 1 ? '' : 's'}`);
  }
  // Mode label
  if (input.isAsync) parts.push('async');
  else if (input.mode === 'solo') parts.push('solo');
  else if (input.status === 'lobby' || input.status === 'waiting') parts.push('lobby');
  else if (input.mode === 'multiplayer') parts.push('live');
  // Players count — only for multi-player
  if (input.mode !== 'solo' && input.playersCount && input.playersCount > 1) {
    parts.push(`${input.playersCount} player${input.playersCount === 1 ? '' : 's'}`);
  }
  return parts.join(' · ');
}
